import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Search, Briefcase, Building2, UserCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const STATUS = { pending: "Pending", in_progress: "In Progress", served: "Served", non_served: "Non-Served", cancelled: "Archived" };

export function GlobalSearch({ open, onClose }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) { setQ(""); setRes(null); return; }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q.trim()) { setRes(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get("/search", { params: { q: q.trim() } }); setRes(data); }
      catch { setRes(null); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (path) => { onClose(); navigate(path); };
  const total = res ? res.jobs.length + res.clients.length + res.servers.length : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl p-0 overflow-hidden top-[15%] translate-y-0" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <div className="flex items-center gap-2 px-4 border-b border-slate-200">
          <Search className="h-4 w-4 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs, names, addresses, case numbers, companies…"
            className="flex-1 h-12 text-sm outline-none bg-transparent" data-testid="global-search-input"
            onKeyDown={(e) => { if (e.key === "Enter" && res?.jobs?.[0]) go(`/jobs/${res.jobs[0].id}`); }} />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          <kbd className="text-[10px] text-slate-400 border border-slate-200 rounded px-1">Esc</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="global-search-results">
          {!q.trim() && <div className="px-4 py-6 text-xs text-slate-400">Type to search across jobs, companies and servers. Press <kbd className="border rounded px-1">/</kbd> anywhere to open search.</div>}
          {q.trim() && res && total === 0 && !loading && <div className="px-4 py-6 text-sm text-slate-500" data-testid="global-search-empty">No matches for “{q}”.</div>}
          {res?.jobs?.length > 0 && <Group icon={Briefcase} title="Jobs">{res.jobs.map((j) => (
            <Item key={j.id} testId={`search-job-${j.id}`} onClick={() => go(`/jobs/${j.id}`)} title={<><span className="font-medium">{j.job_number}</span> · {j.recipient_name}</>} sub={`${j.recipient_address}${j.court_case_number ? ` · ${j.court_case_number}` : ""}`} tag={STATUS[j.status]} />
          ))}</Group>}
          {res?.clients?.length > 0 && <Group icon={Building2} title="Companies">{res.clients.map((c) => (
            <Item key={c.id} testId={`search-client-${c.id}`} onClick={() => go(`/clients`)} title={c.firm_name} sub={c.contact_name} />
          ))}</Group>}
          {res?.servers?.length > 0 && <Group icon={UserCheck} title="Servers">{res.servers.map((s) => (
            <Item key={s.id} testId={`search-server-${s.id}`} onClick={() => go(`/servers`)} title={s.name} sub={s.phone} />
          ))}</Group>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Group({ icon: Icon, title, children }) {
  return (
    <div className="px-2 pb-2">
      <div className="sm-label px-2 py-1 flex items-center gap-1.5"><Icon className="h-3 w-3" />{title}</div>
      {children}
    </div>
  );
}
function Item({ title, sub, tag, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className="w-full text-left px-2 py-2 rounded hover:bg-slate-100 flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-900 truncate">{title}</div>
        {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
      </div>
      {tag && <span className="text-[10px] rounded bg-slate-200 text-slate-700 px-1.5 py-0.5 shrink-0">{tag}</span>}
    </button>
  );
}

export function useGlobalSearchHotkey(setOpen) {
  useEffect(() => {
    const h = (e) => {
      const tag = document.activeElement?.tagName;
      if ((e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(tag)) || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [setOpen]);
}
